import {
  getAmountProductByLines,
  getMethodIndicatorsAndDiscountTotalbyOrders,
  getMoneyByLines,
  getMoneyByMethod,
  getMonitorByCategories,
  getProductsByRangeDate,
} from '@/services/monitoring';
import { translates } from '@/utils';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

interface Props {
  params?: { firstDate?: string; lastDate?: string; isOpen?: boolean };
}

export const useMonitor = ({ params }: Props) => {
  const { isLoading: isLoading4, data: indicatorMethodsAndDiscount } = useQuery({
    queryKey: ['MethodIndicatorsAndDiscount', params?.firstDate, params?.lastDate],
    queryFn: () => getMethodIndicatorsAndDiscountTotalbyOrders(params!),
    select(data) {
      const information: any[] = [];
      let total = 0;
      let indicators = 0;
      const { discount, ...rest } = data;
      Object.keys(rest).forEach((key: string) => {
        Object.keys(data[key]).forEach((keylast) => {
          if (key === 'methods') {
            total += Number(data[key][keylast]);
          } else if (key === 'indicators') {
            total -= Number(data[key][keylast]);
            indicators += Number(data[key][keylast]);
          } else if (keylast === 'tips') {
            total -= Number(data[key][keylast]);
          }
          if (keylast !== 'tips' && key !== 'indicators')
            information.push({ label: keylast, value: data[key][keylast] });
        });
      });
      information.push({ label: 'indicators', value: indicators });
      return {
        data: information,
        originalData: data,
        total,
      };
    },
    enabled: Boolean(params),
    retry: false,
  });

  const notFoodCompanyData = useMemo(
    () =>
      indicatorMethodsAndDiscount?.data.filter(
        (indicator) => indicator.label !== 'tipsCompany' && indicator.label !== 'tipsWaiter'
      ),
    [indicatorMethodsAndDiscount?.data]
  );

  const { isLoading: isLoading3, data: data3 } = useQuery({
    queryKey: ['monitor', 'daily-report', 'type-product', params?.firstDate, params?.lastDate],
    queryFn: () => getAmountProductByLines(params!),
    select(dataTemp) {
      const categories: string[] = [];
      const data: number[] = [];
      dataTemp.forEach((element: any) => {
        categories.push(element.line.toUpperCase());
        data.push(parseInt(element.quantity, 10));
      });
      return {
        categories,
        values: [
          {
            data,
          },
        ],
      };
    },
    enabled: Boolean(params),
    retry: false,
  });

  const { isLoading: isLoading5, data: data5 } = useQuery({
    queryKey: ['monitor', 'daily-report', 'products', params?.firstDate, params?.lastDate],
    queryFn: () => getProductsByRangeDate(params!),
    select(dataTemp) {
      const products: any[] = [];
      const values: any[] = [];

      dataTemp.forEach((element: any) => {
        products.push(element.name);
        values.push(parseInt(element.quantity_total, 10));
      });

      const amountProductsByCategories = {
        categories: products,
        values: [
          {
            data: values,
          },
        ],
      };

      return {
        quantity: amountProductsByCategories,
        sellProducts: dataTemp.map((data: any) => ({
          label: data.name,
          value: parseInt(data.sellLetter_total, 10),
        })),
      };
    },
    enabled: Boolean(params),
    retry: false,
  });

  const { isLoading: isLoading2, data: data2 } = useQuery({
    queryKey: ['monitor', 'daily-report', 'type-money', params?.firstDate, params?.lastDate],
    queryFn: () => getMoneyByMethod(params!),
    select(data) {
      return Object.keys(data).map((key: string) => ({
        label: translates[key].toUpperCase(),
        value: parseInt(data[key] ?? 0, 10),
      }));
    },
    enabled: Boolean(params),
    retry: false,
  });
  const { isLoading: isLoadingCategories, data: dataCategories } = useQuery({
    queryKey: ['monitor', 'daily-report', 'categories', params?.firstDate, params?.lastDate],
    queryFn: getMonitorByCategories(params!),
    select(data) {
      const categories: string[] = [];
      const values: number[] = [];
      data?.amountProductsByCategories.forEach((element: any) => {
        categories.push(element.category.toUpperCase());
        values.push(parseInt(element.quantity, 10));
      });
      const amountProductsByCategories = {
        categories,
        values: [
          {
            data: values,
          },
        ],
      };
      const moneyByCategories =
        data?.moneyByCategories?.map((item: any) => ({
          label: item?.category?.toUpperCase() ?? '',
          value: parseInt(item?.total ?? 0, 10),
        })) ?? [];
      return {
        amountProductsByCategories,
        moneyByCategories,
      };
    },
    enabled: Boolean(params),
    retry: false,
  });
  const { isLoading, data } = useQuery({
    queryKey: ['monitor', 'daily-report', 'lines', params?.firstDate, params?.lastDate],
    queryFn: () => getMoneyByLines(params!),
    select(result) {
      return result.map((item: any) => ({
        label: item.line.toUpperCase(),
        value: parseInt(item.total, 10),
      }));
    },
    enabled: Boolean(params),
    retry: false,
  });

  const pieTaxes = useMemo(() => {
    const pieTaxesData = indicatorMethodsAndDiscount?.originalData?.indicators ?? {};
    const pieTaxesDataArray = Object.keys(pieTaxesData)?.map?.((key: string) => ({
      label: key,
      value: parseInt(pieTaxesData[key] ?? 0, 10),
    }));
    return pieTaxesDataArray;
  }, [indicatorMethodsAndDiscount?.originalData?.indicators]);

  return {
    isLoading,
    isLoading2,
    isLoading3,
    isLoading4,
    isLoading5,
    isLoadingCategories,
    data,
    indicatorMethodsAndDiscount,
    data2,
    data3,
    dataCategories,
    data5,
    pieTaxes,
    notFoodCompanyData,
  };
};
